/**
 * F104 — Tabla de pagos reutilizable.
 *
 * Usada por:
 *   - PagosListPage (toda la lista del sistema)
 *   - ClienteDetailPage tab Pagos (filtrada por cliente_id)
 *   - ContratoDetailPage tab Pagos (filtrada por contrato_id)
 *
 * Las columnas se adaptan al contexto: si filtras por cliente, oculta
 * la columna "Cliente"; si filtras por contrato, oculta "Cliente" y
 * muestra el folio de la factura como link.
 */
import { useState } from "react";
import { Link } from "react-router-dom";
import { Ban, ExternalLink, Wallet, Loader2, Printer } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Table, TableBody, TableCell, TableEmpty, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { EmptyState } from "@/components/EmptyState";
import { formatMXN } from "@/lib/utils";
import { useAnularPago, usePagos, type PagosFilters } from "./api";
import type { Pago } from "./schemas";
import type { ApiError } from "@/lib/api";
import { useAuthStore } from "@/lib/auth";
import { abrirReciboPago } from "@/features/config/api";
// rc139 — móvil: tarjetas
import { useIsMobile } from "@/hooks/useIsMobile";
import { MobileCard, MobileEmpty, MobileList, MobileMeta } from "@/components/ui/mobile-list";

type Props = {
  /** Filtro base — fija el contexto (cliente_id o contrato_id) */
  filters: PagosFilters;
  /** Si se pasa cliente_id en filters, oculta la columna Cliente */
  hideClienteColumn?: boolean;
  /** Mostrar paginador (off por default cuando es tab embebido) */
  showPagination?: boolean;
};

export function PagosTable({
  filters,
  hideClienteColumn = false,
  showPagination = true,
}: Props) {
  const [page, setPage] = useState(1);
  const [anulando, setAnulando] = useState<Pago | null>(null);

  // rc34: cajero NO debe ver el botón "anular pago" (backend lo rechaza igual,
  // pero ocultarlo evita que clickee y reciba 403 confuso).
  const canAnular = useAuthStore((s) => s.hasPermission("pagos.anular"));

  const { data, isLoading, isFetching } = usePagos({ ...filters, page, size: filters.size ?? 50 });
  const items = data?.items ?? [];
  const totalPages = data ? Math.ceil(data.total / (data.size || 50)) : 1;
  const isMobile = useIsMobile();

  const paginador = showPagination && totalPages > 1 && (
    <div className="flex items-center justify-between border-t px-3 py-2 text-xs">
      <span className="text-muted-foreground">
        Página {data?.page ?? 1} de {totalPages}
        {isFetching && " · actualizando…"}
      </span>
      <div className="flex gap-1">
        <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>
          Anterior
        </Button>
        <Button size="sm" variant="outline" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>
          Siguiente
        </Button>
      </div>
    </div>
  );

  if (isMobile) {
    return (
      <>
        {isLoading && items.length === 0 ? (
          <MobileEmpty><Loader2 size={14} className="mr-1 inline animate-spin" /> Cargando pagos…</MobileEmpty>
        ) : items.length === 0 ? (
          <MobileEmpty>Sin pagos registrados. Cuando se aplique un pago, aparecerá aquí.</MobileEmpty>
        ) : (
          <MobileList>
            {items.map((p) => (
              <MobileCard
                key={p.id}
                className={p.anulado ? "opacity-50" : ""}
                title={<span className="font-mono">{p.folio}</span>}
                subtitle={<>{p.fecha} · <span className="capitalize">{p.metodo}</span></>}
                right={
                  <span className="font-mono text-sm font-semibold">
                    {formatMXN(Number(p.monto))}
                    {p.anulado && <Badge variant="destructive" className="ml-1 text-[9px]">ANULADO</Badge>}
                  </span>
                }
                actions={
                  !p.anulado ? (
                    <>
                      <Button variant="ghost" size="sm" onClick={() => abrirReciboPago(p.id).catch((e) => toast.error("Error al abrir recibo", { description: e.message }))}>
                        <Printer size={13} /> Recibo
                      </Button>
                      {canAnular && (
                        <Button variant="ghost" size="sm" className="text-destructive" onClick={() => setAnulando(p)}>
                          <Ban size={13} /> Anular
                        </Button>
                      )}
                    </>
                  ) : undefined
                }
              >
                {!hideClienteColumn && (
                  <MobileMeta label="Cliente"><Link to={`/clientes/${p.cliente_id}`} className="text-primary">ver cliente</Link></MobileMeta>
                )}
                <MobileMeta label="Factura">
                  {p.factura_id ? <Link to={`/facturas/${p.factura_id}`} className="text-primary">ver factura</Link> : <span className="italic text-muted-foreground">crédito</span>}
                </MobileMeta>
                <MobileMeta label="Cajero">{p.registrado_por_nombre || "—"}</MobileMeta>
              </MobileCard>
            ))}
          </MobileList>
        )}
        {paginador && <div className="mt-2 rounded-lg border bg-card">{paginador}</div>}
        {anulando && <AnularPagoDialog pago={anulando} onClose={() => setAnulando(null)} />}
      </>
    );
  }

  return (
    <>
      <div className="rounded-lg border bg-card">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Folio</TableHead>
              <TableHead>Fecha</TableHead>
              {!hideClienteColumn && <TableHead>Cliente</TableHead>}
              <TableHead>Factura</TableHead>
              <TableHead>Método</TableHead>
              <TableHead>Cajero</TableHead>
              <TableHead className="text-right">Monto</TableHead>
              <TableHead className="text-right pr-4 w-[60px]"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading && items.length === 0 ? (
              <TableEmpty>
                <div className="flex items-center justify-center gap-2 py-4 text-xs text-muted-foreground">
                  <Loader2 size={14} className="animate-spin" /> Cargando pagos…
                </div>
              </TableEmpty>
            ) : items.length === 0 ? (
              <TableEmpty>
                <EmptyState
                  icon={Wallet}
                  title="Sin pagos registrados"
                  description="Cuando se aplique un pago, aparecerá aquí."
                />
              </TableEmpty>
            ) : (
              items.map((p) => (
                <TableRow key={p.id} className={p.anulado ? "opacity-50" : ""}>
                  <TableCell className="font-mono text-xs">{p.folio}</TableCell>
                  <TableCell className="text-xs text-muted-foreground">{p.fecha}</TableCell>
                  {!hideClienteColumn && (
                    <TableCell>
                      <Link
                        to={`/clientes/${p.cliente_id}`}
                        className="text-primary hover:underline text-xs"
                      >
                        ver cliente
                      </Link>
                    </TableCell>
                  )}
                  <TableCell>
                    {p.factura_id ? (
                      <Link
                        to={`/facturas/${p.factura_id}`}
                        className="text-primary hover:underline text-xs flex items-center gap-1"
                      >
                        factura <ExternalLink size={10} />
                      </Link>
                    ) : (
                      <span className="text-[10px] text-muted-foreground italic">
                        crédito
                      </span>
                    )}
                  </TableCell>
                  <TableCell className="capitalize text-xs">{p.metodo}</TableCell>
                  <TableCell className="text-xs">
                    {p.registrado_por_nombre || (
                      <span className="text-muted-foreground italic">—</span>
                    )}
                  </TableCell>
                  <TableCell className="text-right font-mono text-sm">
                    {formatMXN(Number(p.monto))}
                    {p.anulado && (
                      <Badge variant="destructive" className="ml-1 text-[9px]">
                        ANULADO
                      </Badge>
                    )}
                  </TableCell>
                  <TableCell className="text-right pr-4">
                    {!p.anulado && (
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => {
                          abrirReciboPago(p.id).catch((e) =>
                            toast.error("Error al abrir recibo", { description: e.message })
                          );
                        }}
                        title="Imprimir recibo"
                      >
                        <Printer size={14} />
                      </Button>
                    )}
                    {!p.anulado && canAnular && (
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => setAnulando(p)}
                        title="Anular pago"
                      >
                        <Ban size={14} className="text-destructive" />
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>

        {paginador}
      </div>

      <AnularPagoDialog pago={anulando} onClose={() => setAnulando(null)} />
    </>
  );
}

// ─── Modal de anular pago (copiado de PagosListPage para reuso) ─────────
function AnularPagoDialog({ pago, onClose }: { pago: Pago | null; onClose: () => void }) {
  const [motivo, setMotivo] = useState("");
  const [error, setError] = useState<string | null>(null);
  const anular = useAnularPago();

  if (!pago) return null;

  // rc88 — Pago de Toku NO es anulable (Toku no revierte el cobro). El backend
  // ya lo bloquea; aquí mostramos el aviso de una vez, sin campo ni confirmar.
  const esToku = (pago.payment_provider || "").toLowerCase() === "toku";

  return (
    <Dialog open={Boolean(pago)} onOpenChange={(v) => !v && onClose()}>
      <DialogHeader>
        <DialogTitle>Anular pago {pago.folio}</DialogTitle>
        <DialogDescription>
          {esToku
            ? `${formatMXN(Number(pago.monto))} · cobrado mediante Toku.`
            : `${formatMXN(Number(pago.monto))} · ${pago.metodo}. La factura asociada recuperará el saldo y podría volver a estar pendiente.`}
        </DialogDescription>
      </DialogHeader>
      <DialogContent>
        {esToku ? (
          <div className="flex items-start gap-1 rounded-md border border-destructive/50 bg-destructive/10 p-2 text-xs text-destructive">
            <span>
              Este pago no se puede anular porque fue registrado mediante Toku.
              Un cobro ya procesado por Toku no puede revertirse.
            </span>
          </div>
        ) : (
          <>
            <Label>Motivo de anulación *</Label>
            <Textarea
              rows={3}
              value={motivo}
              onChange={(e) => setMotivo(e.target.value)}
              placeholder="Ej. Cheque devuelto, error en captura…"
            />
            {error && <div className="mt-2 text-xs text-destructive">{error}</div>}
          </>
        )}
      </DialogContent>
      <DialogFooter>
        <Button variant="ghost" size="sm" onClick={onClose}>
          {esToku ? "Cerrar" : "Cancelar"}
        </Button>
        {!esToku && (
          <Button
            variant="destructive"
            size="sm"
            disabled={anular.isPending || motivo.trim().length < 5}
            onClick={async () => {
              setError(null);
              try {
                await anular.mutateAsync({ id: pago.id, motivo: motivo.trim() });
                toast.success("Pago anulado", {
                  description: `Folio ${pago.folio} marcado como anulado.`,
                });
                setMotivo("");
                onClose();
              } catch (e) {
                setError((e as ApiError).message ?? "Error al anular");
              }
            }}
          >
            {anular.isPending ? "Anulando…" : "Anular pago"}
          </Button>
        )}
      </DialogFooter>
    </Dialog>
  );
}
